import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import { TIMER_DURATION, useNotificationSendTimerStore } from 'src/store/notifications/notification-send-timer.store';
import { useNotificationStore } from 'src/store/notifications/notifications.store';

// Componente individual para cada snackbar de timer
function TimerSnackbarItem({
  campaignId,
  campaignTitle,
  remainingTime,
  index,
}: {
  campaignId: string;
  campaignTitle: string;
  remainingTime: number;
  index: number;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const cancelNotification = useNotificationSendTimerStore((state) => state.cancelNotification);
  const fetchCampaigns = useNotificationStore((state) => state.fetchCampaigns);

  const handleCancel = async () => {
    try {
      await cancelNotification(campaignId);
      enqueueSnackbar('Envio da notificação cancelado!', { variant: 'warning' });

      // Atualiza a tabela de notificações se o usuário ainda estiver nela
      if (location.pathname === '/dashboard/notifications' || location.pathname === '/dashboard/notifications/') {
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Erro ao cancelar notificação:', error);
      enqueueSnackbar('Erro ao cancelar notificação', { variant: 'error' });
    }
  };

  const progress = (remainingTime / TIMER_DURATION) * 100;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
        p: 2,
        minWidth: 350,
        maxWidth: 450,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {campaignTitle || 'Notificação'} em processamento...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {remainingTime}s
        </Typography>
      </Box>

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 1.5, borderRadius: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          Deseja cancelar esta operação?
        </Typography>
        <Button size="small" color="error" variant="soft" onClick={handleCancel}>
          Cancelar envio
        </Button>
      </Box>
    </Box>
  );
}

// Componente interno que se subscreve ao store
function SnackbarContent() {
  const activeTimers = useNotificationSendTimerStore((state) => state.activeTimers);

  if (activeTimers.length === 0) return null;

  return (
    <Stack
      spacing={1.5}
      sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
      }}
    >
      {activeTimers.map((timer, index) => (
        <TimerSnackbarItem
          key={timer.campaignId}
          campaignId={timer.campaignId}
          campaignTitle={timer.campaignData.title}
          remainingTime={timer.remainingTime}
          index={index}
        />
      ))}
    </Stack>
  );
}

// Componente principal que renderiza o conteúdo
export function NotificationSendTimerSnackbar() {
  return <SnackbarContent />;
}
